import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Flag } from 'lucide-react';
import './FeatureFlagList.css';

function FeatureFlagList() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const navigate = useNavigate();

  const fetchFlags = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/experiments', {
        params: { type: 'feature_flag' },
      });
      setFlags(response.data.experiments || response.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch feature flags:', err);
      setError('Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  const handleToggle = async (flag) => {
    setTogglingId(flag.id);
    try {
      const action = flag.status === 'running' ? 'pause' : 'start';
      await axios.post(`/api/experiments/${flag.id}/lifecycle`, { action });
      await fetchFlags();
    } catch (err) {
      console.error('Failed to toggle flag:', err);
    } finally {
      setTogglingId(null);
    }
  };

  const handleRolloutChange = async (flag, newValue) => {
    try {
      await axios.put(`/api/experiments/${flag.id}`, {
        traffic_percentage: parseInt(newValue),
      });
      setFlags((prev) =>
        prev.map((f) =>
          f.id === flag.id ? { ...f, traffic_percentage: parseInt(newValue) } : f
        )
      );
    } catch (err) {
      console.error('Failed to update rollout:', err);
    }
  };

  if (loading) {
    return (
      <div className="feature-flag-list">
        <div className="exp-loading">
          <div className="spinner"></div>
          <p>Loading feature flags...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feature-flag-list">
        <div className="exp-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="feature-flag-list">
      <div className="exp-page-header">
        <div>
          <h1>Feature Flags</h1>
          <p className="exp-page-subtitle">Toggle features on and off</p>
        </div>
        <button
          className="exp-btn-primary"
          onClick={() => navigate('/experiments/new')}
        >
          <Plus size={16} />
          New Feature Flag
        </button>
      </div>

      {flags.length === 0 ? (
        <div className="ff-empty">
          <Flag size={40} className="ff-empty-icon" />
          <p>No feature flags yet</p>
          <button
            className="exp-btn-primary"
            onClick={() => navigate('/experiments/new')}
          >
            Create your first flag
          </button>
        </div>
      ) : (
        <table className="exp-table ff-table">
          <thead>
            <tr>
              <th>Key</th>
              <th>Name</th>
              <th>Site</th>
              <th>Status</th>
              <th>Rollout %</th>
              <th>Toggle</th>
            </tr>
          </thead>
          <tbody>
            {flags.map((flag) => {
              const isOn = flag.status === 'running';
              return (
                <tr key={flag.id} className="ff-row">
                  <td>
                    <span className="ff-key">{flag.key}</span>
                  </td>
                  <td className="ff-name">{flag.name}</td>
                  <td className="ff-site">{flag.site || '--'}</td>
                  <td>
                    <span className={`exp-status-badge exp-status-${flag.status}`}>
                      {flag.status}
                    </span>
                  </td>
                  <td>
                    <div className="ff-rollout">
                      <input
                        type="number"
                        className="ff-rollout-input"
                        value={flag.traffic_percentage ?? 100}
                        onChange={(e) => handleRolloutChange(flag, e.target.value)}
                        min="0"
                        max="100"
                      />
                      <span className="ff-rollout-pct">%</span>
                    </div>
                  </td>
                  <td>
                    <button
                      className={`ff-toggle ${isOn ? 'on' : 'off'}`}
                      onClick={() => handleToggle(flag)}
                      disabled={togglingId === flag.id}
                      title={isOn ? 'Turn off' : 'Turn on'}
                    >
                      <div className="ff-toggle-knob" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default FeatureFlagList;
